import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { InputSkeleton } from '@delmonicos/shared-design-system-react/src/components/skeleton/input-skeleton';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Toggle } from '@delmonicos/shared-design-system-react/src/components/toggle/toggle';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { Language } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/language.enum';
import { UserPreferencesModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/outputs/user-preferences.model';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import i18n from '@app/core/locale/i18n';
import {
  useCpmsBoGetMeQuery,
  useCpmsBoUpdatePreferencesMutation,
} from '@service/cpms-bo-user-me.service';

const TICKET_NOTIFICATION_PREFERENCES: {
  field: keyof UserPreferencesModel;
  labelKey: string;
}[] = [
  {
    field: 'subscribeToTicketOpeningNotifications',
    labelKey:
      'delmonicos-shared-sdk.outputs-user-preferences.subscribe-to-ticket-opening-notifications',
  },
  {
    field: 'subscribeToTicketResolutionNotifications',
    labelKey:
      'delmonicos-shared-sdk.outputs-user-preferences.subscribe-to-ticket-resolution-notifications',
  },
  {
    field: 'subscribeToTicketUpdateNotifications',
    labelKey:
      'delmonicos-shared-sdk.outputs-user-preferences.subscribe-to-ticket-update-notifications',
  },
];

export const SettingsController = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { data: user, isFetching } = useCpmsBoGetMeQuery();
  const [handleUpdate, { isLoading: isUpdating }] =
    useCpmsBoUpdatePreferencesMutation();
  const userLanguage = user?.language || Language.fr;

  const handleLanguageChange = (value?: string) => {
    if (!value || !user) {
      return;
    }
    let i18nLanguage = value.toString();
    if (value === Language.fr) {
      i18nLanguage = 'fr-FR';
    }
    handleUpdate({
      language: value as Language,
    })
      .unwrap()
      .then(() => {
        i18n.changeLanguage(i18nLanguage);
        toast.success(
          t('$.settings.language-edit.success', {
            language: t(`delmonicos-shared-sdk.enums-language.${value}`),
          }),
        );
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.settings.language-edit.error', { error: errorMessage }),
        );
      });
  };

  const handleTicketNotificationChange = (
    field: keyof UserPreferencesModel,
    value: boolean,
  ) => {
    handleUpdate({ [field]: value })
      .unwrap()
      .then(() => {
        toast.success(t('$.settings.ticket-notifications-edit.success'));
      })
      .catch((error) => {
        toast.error(
          t('$.settings.ticket-notifications-edit.error', {
            error: error?.data?.message,
          }),
        );
      });
  };

  return (
    <Stack direction="column" gap={GapEnum.M}>
      <Card gap={GapEnum.M}>
        <AccentTitle
          icon="flag-04"
          text={t('$.settings.language.title')}
          variant={AccentVariantEnum.Primary}
        />
        <Dropdown value={userLanguage} onChange={handleLanguageChange}>
          <option value={Language.en}>
            {t('delmonicos-shared-sdk.enums-language.en')}
          </option>
          <option value={Language.fr}>
            {t('delmonicos-shared-sdk.enums-language.fr')}
          </option>
          <option value={Language.it}>
            {t('delmonicos-shared-sdk.enums-language.it')}
          </option>
        </Dropdown>
      </Card>
      <Card gap={GapEnum.M}>
        <AccentTitle
          icon="bell-03"
          text={t('$.settings.ticket-notifications.title')}
          variant={AccentVariantEnum.Secondary}
        />
        {user ? (
          <Stack direction="column" gap={GapEnum.M}>
            {TICKET_NOTIFICATION_PREFERENCES.map(({ field, labelKey }) => (
              <Toggle
                key={field}
                id={field}
                label={t(labelKey)}
                checked={user.userPreferences?.[field] ?? false}
                isDisabled={isUpdating || isFetching}
                onChange={(value) =>
                  handleTicketNotificationChange(field, value)
                }
              />
            ))}
          </Stack>
        ) : (
          <>
            <InputSkeleton />
            <InputSkeleton />
            <InputSkeleton />
          </>
        )}
      </Card>
    </Stack>
  );
};
